// React and React Router
import { StrictMode } from "react";
import { RouterProvider } from "react-router-dom";

// Third-party libraries
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { StytchProvider } from "@stytch/react";
import { StytchClientOptions, StytchUIClient } from "@stytch/vanilla-js";
import i18n from "i18next";
import HttpApi from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import { getBrowserPreferredLanguage } from "./lib/utils/browserLanguage";

// Router
import { router } from "./lib/router";

// Components
import { Toaster } from "./components/ui/toaster";
import Loading from "./components/Loading";

// Contexts
import { DevModeProvider } from "./lib/contexts/DevModeContext";
import { A11yProvider, useA11y } from "./lib/contexts/A11yProvider";

// Styles
import "./index.css";
import { MotionConfig } from "motion/react";
import { getDisplayLanguageCodes } from "./lib/constants/languages";
import { useTranslationLoader } from "./hooks/useTranslationLoader";
const stytchOptions = {
  cookieOptions: {
    opaqueTokenCookieName: "lesson_planner_session",
    jwtCookieName: "lesson_planner_jwt",
    istCookieName: "lesson_planner_ist",
  },
} as StytchClientOptions;

// Sentry is already imported above

try {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tunnel: import.meta.env.VITE_SENTRY_TUNNEL,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    environment:
      import.meta.env.VITE_API_URL === "https://dev.api.tchr.tools"
        ? import.meta.env.DEV
          ? "local"
          : "development"
        : "production",

    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
} catch (error) {
  console.error("Sentry initialization failed:", error);
}

i18n
  .use(HttpApi)
  .use(initReactI18next)
  .init(
    {
      lng: getBrowserPreferredLanguage(),
      fallbackLng: "en",
      supportedLngs: getDisplayLanguageCodes(),
      ns: [
        "common",
        "landingPage",
        "newLanding",
        "communicationGenerator",
        "resourceCustomiser",
        "researcher",
        "catchupCoach",
        "lessonLab",
        "settings",
        "pricing",
        "auth",
        "results",
        "sitemap",
        "templates",
        "errors",
      ],
      defaultNS: "common",
      interpolation: {
        escapeValue: false,
      },
      debug: import.meta.env.DEV,
      backend: {
        loadPath: `${
          import.meta.env.DEV
            ? "https://localhost:5174/locales/{{lng}}/{{ns}}.json"
            : `${import.meta.env.VITE_LANGUAGE_URL}`
        }`,
      },

      saveMissing: import.meta.env.DEV,
      saveMissingPlurals: import.meta.env.DEV,
      react: {
        useSuspense: false,
      },
    },
    (error) => {
      if (error) {
        console.error("i18n initialization failed:", error);
      }
    }
  );

const stytchClient = new StytchUIClient(
  import.meta.env.VITE_STYTCH_PUBLIC_TOKEN, // or process.env.STYTCH_PUBLIC_TOKEN for non-Vite based projects
  stytchOptions
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      experimental_prefetchInRender: true,
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60 * 24,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error as unknown, {
        tags: {
          source: "react-query",
          kind: "query",
          key: query.queryKey.join("."),
        },
        extra: { queryKey: query.queryKey },
      });
      console.error("Query error:", error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      Sentry.captureException(error as unknown, {
        tags: {
          source: "react-query",
          kind: "mutation",
          key: mutation.options.mutationKey?.join("."),
        },
        extra: { mutationKey: mutation.options.mutationKey },
      });
      console.error("Mutation error:", error);
    },
  }),
});

const App = () => {
  return (
    <StrictMode>
      <DevModeProvider>
        <QueryClientProvider client={queryClient}>
          <StytchProvider stytch={stytchClient}>
            <A11yProvider>
              <TranslationWrapper />
              {import.meta.env.DEV && (
                <ReactQueryDevtools
                  initialIsOpen={false}
                  buttonPosition="top-right"
                  position="right"
                />
              )}
            </A11yProvider>
          </StytchProvider>
        </QueryClientProvider>
      </DevModeProvider>
    </StrictMode>
  );
};

const TranslationWrapper = () => {
  const { settings } = useA11y();
  const { ready, isLoading, isError, error } = useTranslationLoader(
    settings.language
  );

  // Show loading state until translations are ready
  if (isLoading || !ready) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen">
        <Loading />
      </div>
    );
  }

  // Show error state if translation loading failed
  if (isError && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="text-center text-red-500">
          <h2 className="mb-2 text-xl font-semibold">
            Translation Loading Error
          </h2>
          <p>{error.message}</p>
          <p className="mt-2 text-sm text-gray-500">
            The app will continue with fallback translations.
          </p>
        </div>
        <AppContent />
      </div>
    );
  }

  return <AppContent />;
};

const AppContent = () => {
  const { settings } = useA11y();

  return (
    <MotionConfig reducedMotion={settings.lowAnimation ? "always" : "user"}>
      <RouterProvider router={router} />
      <Toaster />
    </MotionConfig>
  );
};

export default App;
